import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DropdownComponent, ToastService, SidenavModule, SelectModule, InputGroupModule } from '@msi/cobalt';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ReleaseNote } from 'src/app/interfaces/modal.interface';
import { NewFeatureModalService } from 'src/app/services/new-feature-modal/new-feature-modal.service';
import { NgForm, FormsModule } from '@angular/forms';
import { ApiService } from 'src/app/services/api/api.service';
import { SupportInfoService } from 'src/app/services/support-info/support-info.service';
import { CcAdminApiHelperService } from 'src/app/services/ccAdmin/cc-admin-api-helper.service';
import { UserInfo } from '@msi/commandcentral-common-header';
import { AgreementsModel } from 'src/app/interfaces/privacy-model.interface';
import { AgreementsModalInvokerService } from 'src/app/services/agreements-modal-invoker/agreements-modal-invoker.service';
import { directory } from 'src/assets/release-notes/directory';
import { SafeUrlPipe } from '../../pipes/safe-url/safe-url.pipe';
import { TableModule } from '@msi/cobalt/table';
import { TagModule } from '@msi/cobalt/tag';
import { TooltipModule } from '@msi/cobalt/tooltip';

import { ItemModule } from '@msi/cobalt/item';
import { MatToolbar } from '@angular/material/toolbar';
import { IconModule } from '@msi/cobalt/icon';
import { DropdownModule } from '@msi/cobalt/dropdown';

export const UI_VERSION = directory[directory.length - 1]?.releaseTitle ?? 'ORC version';

@Component({
	selector: 'app-support-info',
	templateUrl: './support-info.component.html',
	styleUrls: ['./support-info.component.scss'],
	standalone: true,
	imports: [
		DropdownModule,
		IconModule,
		MatToolbar,
		SidenavModule,
		ItemModule,
		TooltipModule,
		TagModule,
		TableModule,
		FormsModule,
		SelectModule,
		InputGroupModule,
		SafeUrlPipe,
	],
})
export class SupportInfoComponent implements OnInit, OnDestroy {
	/** Reference to the support dropdown so it can be closed when a feature modal is opened */
	@ViewChild(DropdownComponent) supportDropdown: DropdownComponent;

	readonly copyAgencyNameText = $localize`Copy Agency Name`;
	readonly copyAgencyIdText = $localize`Copy Agency ID`;

	feedbackTypes = [$localize`Question`, $localize`Give feedback`, $localize`Feature request`, $localize`Bug report`];
	titleString = '';
	detailsString = '';
	feedbackType = '';

	userInfo: UserInfo;
	agreements: AgreementsModel[];

	public set isOpen(b: boolean) {
		if (b) {
			this.newFeatureModalService.loadAllReleaseNotes();
		}
	}

	public activeSelection = 'myAgencyInfo';
	public releaseNotes: ReleaseNote[];
	public uiVersion = UI_VERSION;

	agencyNameTooltipText = this.copyAgencyNameText;
	agencyIdTooltipText = this.copyAgencyIdText;

	private destroy: Subject<void> = new Subject();

	constructor(
		private newFeatureModalService: NewFeatureModalService,
		private apiService: ApiService,
		private toastService: ToastService,
		private supportInfoService: SupportInfoService,
		private ccAdminApiHelperService: CcAdminApiHelperService,
		private agreementsModalInvokerService: AgreementsModalInvokerService
	) {}

	ngOnInit() {
		this.newFeatureModalService.releaseNotes$.pipe(takeUntil(this.destroy)).subscribe(data => {
			this.releaseNotes = data;
		});

		// Get agreements from already loaded list of agreements from the modal invoker server
		this.agreementsModalInvokerService.loadingData$.pipe(filter(data => data === false)).subscribe(() => {
			this.agreements = this.agreementsModalInvokerService.agreements;
		});

		// Get user agency info for 'My Agency Info' tab
		this.ccAdminApiHelperService.getUserInfo().then(userInfo => {
			this.userInfo = userInfo;
		});

		// We can call the dropdownOpen() function from SupportInfoService to open/close it from an external component
		this.supportInfoService.dropdownOpened$.pipe(takeUntil(this.destroy)).subscribe(dropdownOpened => {
			if (dropdownOpened) {
				this.activeSelection = 'myAgencyInfo';
				this.supportDropdown?.open();
			} else this.supportDropdown?.close();
		});
	}

	onSubmit(feedbackForm: NgForm) {
		this.apiService.postFeedback(this.feedbackType, this.titleString, this.detailsString).subscribe({
			next: () => {
				feedbackForm.form.reset();
				this.toastService.success($localize`Thank you for your feedback.`, null, { autoDismiss: 5000 });
			},
			error: () => {
				this.toastService.error($localize`Feedback submission failed, please try again.`, null, { autoDismiss: 7000 });
			},
		});
	}

	ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
	}

	openFeatureDescription(i: number) {
		this.supportDropdown?.close();
		this.newFeatureModalService.openModal(i);
	}

	// Gets the HTML element from the ID provided and copies the text to clipboard if ID exists
	copyAgencyDetail(id: string, agencyName: boolean = false) {
		const textToBeCopied = document.getElementById(id);
		if (textToBeCopied) {
			navigator.clipboard.writeText(textToBeCopied.innerHTML);
			if (agencyName) this.agencyNameTooltipText = $localize`Agency name copied`;
			else this.agencyIdTooltipText = $localize`Agency ID copied`;
		}
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
